import React, { useEffect, useState, useRef } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Drawer,
  Descriptions,
  Anchor,
  Tag,
  notification,
  Modal,
  Input,
  Form,
  DatePicker,
} from "antd";
import { ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Table } from "ant-table-extensions";
import http from "../../../../helpers/http";
import helper from "../../../../helpers/helper";
import config from "../../../../config/config";
import _ from "lodash";
import "./style.css";
import moment from "moment";

function DiwaliBonanza2026MonthlyWinners() {
  const { confirm } = Modal;

  // ─── State ────────────────────────────────────────────────────────────────

  const [loading, setloading] = useState(false);
  const [btnloading, setbtnloading] = useState(false);
  const [form] = Form.useForm();
  const [formError, setFormError] = useState({});

  // Selected month string "YYYY-MM" from MonthPicker
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Whether a batch already exists for selectedMonth — controls Generate button
  const [batchExists, setBatchExists] = useState(false);

  // Current batch object returned from the API
  const [batch, setBatch] = useState(null);

  // Winners list displayed in the table
  const [winners, setWinners] = useState([]);

  // Single winner loaded into Drawer
  const [drawerOpen, setdraweropen] = useState(false);
  const [drawerWinner, setDrawerWinner] = useState({});

  // Per-row loading — keyed by winner _id
  const [rejectLoading, setRejectLoading] = useState({});
  const [replacementLoading, setReplacementLoading] = useState({});
  const [verifyLoading, setVerifyLoading] = useState({});

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  // ─── Constants ────────────────────────────────────────────────────────────

  const moduleNamePural = "Dream Big Win Bigger 2026 Monthly Winners";
  const base_url =
    config.api_url + "admin/leads/diwali-bonanza-2026"; //without trailing slash

  const azure_object_url =
    config.azure_object_url + "public/uploads/diwali-bonanza-2026/";
  const { Link } = Anchor;

  const prizeLabels = {
    SMARTPHONE: "Smartphone",
    GOLD_COIN: "Gold Coin",
    AIRBUDS: "Airbuds",
  };

  // ─── API helpers ──────────────────────────────────────────────────────────

  /**
   * Loads monthly winners and batch for the selected month.
   * Calls GET /admin/leads/diwali-bonanza-2026/monthly-winners?month=YYYY-MM
   * If winners exist, sets batchExists=true so Generate button is disabled.
   */
  const loadBatch = async (month) => {
    if (!month) return;

    setloading(true);
    try {
      const res = await http.get(`${base_url}/monthly-winners`, {
        params: { month },
      });

      if (res && res.data) {
        const batchData = res.data.batch;
        const winnersData = res.data.winners || [];

        const formattedWinners = winnersData.map((winner) => ({
          ...winner,
          first_name: winner.entry?.first_name || "",
          last_name: winner.entry?.last_name || "",
          email: winner.entry?.email || "",
        }));

        setBatch(batchData);
        setWinners(formattedWinners);
        setBatchExists(formattedWinners.length > 0);
      } else {
        setBatch(null);
        setWinners([]);
        setBatchExists(false);
      }
    } catch (err) {
      notification.error({
        message:
          err?.response?.data?.message || "Failed to load monthly winners.",
      });
      setBatch(null);
      setWinners([]);
      setBatchExists(false);
    } finally {
      setloading(false);
    }
  };

  /**
   * Generates monthly winners for the selected month.
   * Calls POST /admin/leads/diwali-bonanza-2026/monthly-winners/generate
   * After success, reloads the batch so winners appear and Generate is disabled.
   */
  const generateWinners = async () => {
    if (!selectedMonth) {
      setFormError({ month: "Please select a month" });
      return;
    }

    setFormError({});
    setbtnloading(true);

    try {
      const result = await http.post(`${base_url}/monthly-winners/generate`, {
        month: selectedMonth,
      });

      if (result) {
        notification.success({
          message: "Monthly winners generated successfully.",
        });
        // Immediately reload the batch — disables Generate button automatically
        await loadBatch(selectedMonth);
      }
    } catch (err) {
      notification.error({
        message:
          err?.response?.data?.message || "Failed to generate monthly winners.",
      });
    } finally {
      setbtnloading(false);
    }
  };

  /**
   * Loads full winner details and opens the Drawer.
   * Calls GET /admin/leads/diwali-bonanza-2026/monthly-winners/:id
   */
  const loadWinner = async (id) => {
    try {
      const res = await http.get(`${base_url}/monthly-winners/${id}`);

      if (res && res.data) {
        setDrawerWinner(res.data);
        setdraweropen(true);
      }
    } catch (err) {
      notification.error({
        message:
          err?.response?.data?.message || "Failed to load winner details.",
      });
    }
  };

  /**
   * Shows a confirmation modal then calls POST /monthly-winners/:id/reject.
   * Reloads the current month's batch after success.
   * Calls POST /admin/leads/diwali-bonanza-2026/monthly-winners/:id/reject
   */
  const rejectWinner = (id) => {
    confirm({
      title: "Are you sure you want to reject this winner?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",

      onOk: async () => {
        setRejectLoading((prev) => ({ ...prev, [id]: true }));
        try {
          const result = await http.post(
            `${base_url}/monthly-winners/${id}/reject`
          );

          if (result) {
            notification.success({
              message: "Winner rejected successfully.",
            });
            await loadBatch(selectedMonth);
          }
        } catch (err) {
          notification.error({
            message:
              err?.response?.data?.message || "Failed to reject winner.",
          });
        } finally {
          setRejectLoading((prev) => ({ ...prev, [id]: false }));
        }
      },
      onCancel() { },
    });
  };

  /**
   * Shows a confirmation modal then calls POST /monthly-winners/:id/verify.
   * Only available when winner is pending (not verified, not rejected).
   * Calls POST /admin/leads/diwali-bonanza-2026/monthly-winners/:id/verify
   */
  const verifyWinner = (id) => {
    confirm({
      title: "Verify this winner?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "Verify",
      okType: "primary",
      cancelText: "Cancel",

      onOk: async () => {
        setVerifyLoading((prev) => ({ ...prev, [id]: true }));
        try {
          const result = await http.post(
            `${base_url}/monthly-winners/${id}/verify`
          );

          if (result) {
            notification.success({
              message: "Winner verified successfully.",
            });
            await loadBatch(selectedMonth);
          }
        } catch (err) {
          notification.error({
            message:
              err?.response?.data?.message || "Failed to verify winner.",
          });
        } finally {
          setVerifyLoading((prev) => ({ ...prev, [id]: false }));
        }
      },
      onCancel() { },
    });
  };

  /**
   * Shows a confirmation modal then calls POST /monthly-winners/:id/replacement.
   * Only available when winner is rejected and replacement not yet generated.
   * Calls POST /admin/leads/diwali-bonanza-2026/monthly-winners/:id/replacement
   */
  const replacementWinner = (id) => {
    confirm({
      title: "Generate replacement winner?",
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: "Generate",
      okType: "primary",
      cancelText: "Cancel",

      onOk: async () => {
        setReplacementLoading((prev) => ({ ...prev, [id]: true }));
        try {
          const result = await http.post(
            `${base_url}/monthly-winners/${id}/replacement`
          );

          if (result) {
            notification.success({
              message: "Replacement winner generated successfully.",
            });
            await loadBatch(selectedMonth);
          }
        } catch (err) {
          notification.error({
            message:
              err?.response?.data?.message ||
              "Failed to generate replacement winner.",
          });
        } finally {
          setReplacementLoading((prev) => ({ ...prev, [id]: false }));
        }
      },
      onCancel() { },
    });
  };

  // ─── Month change handler ─────────────────────────────────────────────────

  /**
   * When admin changes the month, immediately call GET to check whether a
   * batch already exists. Populates the table and disables Generate if so.
   */
  const handleMonthChange = async (date, datestring) => {
    setSelectedMonth(datestring);
    setBatchExists(false);
    setWinners([]);
    setBatch(null);
    setFormError({});

    if (datestring) {
      await loadBatch(datestring);
    }
  };

  // ─── Form submit handler ──────────────────────────────────────────────────

  // const handleSubmit = async () => {
  //   await generateWinners();
  // };
  const handleSubmit = () => {
    if (!selectedMonth) {
      setFormError({ month: "Please select a month" });
      return;
    }
  
    confirm({
      title: "Are you sure you want to generate monthly winners?",
      icon: <ExclamationCircleOutlined />,
      content: `Monthly winners will be generated for ${moment(
        selectedMonth,
        "YYYY-MM"
      ).format("MMMM YYYY")}. This action cannot be undone.`,
      okText: "Generate",
      okType: "primary",
      cancelText: "Cancel",
  
      onOk: async () => {
        await generateWinners();
      },
  
      onCancel() {},
    });
  };

  // ─── Drawer close ─────────────────────────────────────────────────────────

  const handleDrawerClose = () => {
    setDrawerWinner({});
    setdraweropen(false);
  };

  // ─── Column search helpers (same as existing 2026 pages) ──────────────────

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      return (
        <div style={{ padding: 8 }}>
          <Input
            autoFocus
            placeholder="Type text here"
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
              confirm({ closeDropdown: false });
            }}
            onPressEnter={() => {
              confirm();
            }}
            onBlur={() => {
              confirm();
            }}
            style={{ marginBottom: 8, display: "block" }}
          ></Input>
          <Space></Space>
        </div>
      );
    },
    filterIcon: () => {
      return <SearchOutlined />;
    },
    onFilter: (value, record) => {
      return String(record[dataIndex] || "")
        .toLowerCase()
        .includes(value.toLowerCase());
    },
  });

  // ─── Table columns ────────────────────────────────────────────────────────

  const columns = [
    // {
    //   title: "Name",
    //   key: "name",
    //   ellipsis: true,
    //   render: (text, record) => {
    //     const first = record.first_name || "";
    //     const last = record.last_name || "";
    //     const name = `${first} ${last}`.trim();
    //     return name || "NA";
    //   },
    // },
    {
      title: "First Name",
      key: "first_name",
      dataIndex: "first_name",
      render: (tect, record) => {
        return record.first_name
      }
    },
    {
      title: "Last Name",
      key: "last_name",
      dataIndex: "last_name",
      render: (tect, record) => {
        return record.last_name
      }
    },
    {
      title: "Email Id",
      dataIndex: "email",
      key: "email",
      render: (text, record) => {
        return record.entry?.email || "NA";
      },
    },
    {
      title: "Prize",
      dataIndex: "prize",
      key: "prize",
      render: (text, record) => {
        return prizeLabels[record.prize] || record.prize || "NA";
      },
    },
    // {
    //   title: "Generated Date",
    //   key: "generatedDate",
    //   render: (text, record) => {
    //     return record.createdAt
    //       ? helper.UserDate(record.createdAt, "-04:00")
    //       : "NA";
    //   },
    // },
    {
      title: "Status",
      key: "status",
      render: (text, record) => {
        if (record.is_verified) {
          return <Tag color="green">Verified</Tag>;
        }
        if (record.is_rejected) {
          return <Tag color="red">Rejected</Tag>;
        }
        return <Tag color="orange">Pending</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => {
        const isVerified = record.is_verified === true;
        const isRejected = record.is_rejected === true;
        const replacementGenerated = record.replacement_generated === true;

        return (
          <Space size="middle">
            {/* View — always enabled; opens Drawer with full details */}
            <Button
              onClick={() => {
                loadWinner(record._id);
              }}
            >
              View
            </Button>

            {/* Reject — disabled when already verified or already rejected */}
            <Button
              type="danger"
              disabled={isVerified || isRejected}
              loading={rejectLoading[record._id] || false}
              onClick={() => {
                rejectWinner(record._id);
              }}
            >
              Reject
            </Button>

            {/*
              Replacement — enabled ONLY when:
              - winner is rejected
              - winner is not verified
              - replacement has not already been generated
            */}
            <Button
              disabled={!isRejected || isVerified || replacementGenerated}
              loading={replacementLoading[record._id] || false}
              onClick={() => {
                replacementWinner(record._id);
              }}
            >
              Replacement
            </Button>

            {/* Verify — enabled ONLY when winner is pending (not verified, not rejected) */}
            <Button
              type="primary"
              disabled={isVerified || isRejected}
              loading={verifyLoading[record._id] || false}
              onClick={() => verifyWinner(record._id)}
            >
              Verify
            </Button>
          </Space>
        );
      },
    },
  ];

  // ─── Export fields ────────────────────────────────────────────────────────

  const fields: ITableExportFields = {
  month: {
    header: "Month",
    formatter: (_fieldValue, record) => {
      return record.batch?.month
        ? moment(record.batch.month, "YYYY-MM").format("MMMM YYYY")
        : "NA";
    },
  },

  first_name: {
    header: "First Name",
    formatter: (_fieldValue, record) => {
      return record.entry?.first_name || "NA";
    },
  },

  last_name: {
    header: "Last Name",
    formatter: (_fieldValue, record) => {
      return record.entry?.last_name || "NA";
    },
  },

  phone: {
    header: "Contact Number",
    formatter: (_fieldValue, record) => {
      if (record.entry?.country_code && record.entry?.phone) {
        return `${record.entry.country_code} ${record.entry.phone}`;
      }

      if (record.entry?.phone) {
        return record.entry.phone;
      }

      return "NA";
    },
  },

  email: {
    header: "Email Id",
    formatter: (_fieldValue, record) => {
      return record.entry?.email || "NA";
    },
  },

  gender: {
    header: "Gender",
    formatter: (_fieldValue, record) => {
      return record.entry?.gender || "NA";
    },
  },

  date_of_birth: {
    header: "Date of Birth",
    formatter: (_fieldValue, record) => {
      return record.entry?.date_of_birth
        ? `="${moment(record.entry.date_of_birth).format("DD-MMM-YYYY")}"`
        : "NA";
    },
  },

  address: {
    header: "Address",
    formatter: (_fieldValue, record) => {
      return record.entry?.address || "NA";
    },
  },

  country: {
    header: "Country",
    formatter: (_fieldValue, record) => {
      return record.entry?.country || "NA";
    },
  },

  state: {
    header: "State",
    formatter: (_fieldValue, record) => {
      return record.entry?.state || "NA";
    },
  },

  city: {
    header: "City",
    formatter: (_fieldValue, record) => {
      return record.entry?.city
        ? _.replace(_.deburr(record.entry.city), /'/g, "")
        : "NA";
    },
  },

  zip: {
    header: "Zip Code",
    formatter: (_fieldValue, record) => {
      return record.entry?.zip || "NA";
    },
  },

  store: {
    header: "Which store did you buy this Laxmi product from?",
    formatter: (_fieldValue, record) => {
      return record.entry?.store || "NA";
    },
  },

  campaign_source: {
    header: "How did you get to know about this campaign?",
    formatter: (_fieldValue, record) => {
      return record.entry?.campaign_source || "NA";
    },
  },

  laxmi_fav_product: {
    header: "What is your favorite product from Laxmi?",
    formatter: (_fieldValue, record) => {
      return record.entry?.laxmi_fav_product || "NA";
    },
  },

  laxmi_brand_one_word: {
    header: "Can you Describe Laxmi Brand in one word?",
    formatter: (_fieldValue, record) => {
      return record.entry?.laxmi_brand_one_word || "NA";
    },
  },

  laxmi_ads_desc: {
    header: "Have you seen Laxmi ads before? if yes where?",
    formatter: (_fieldValue, record) => {
      return record.entry?.laxmi_ads_desc || "NA";
    },
  },

  invoice: {
    header: "Invoice",
    formatter: (_fieldValue, record) => {
      return record.entry?.invoice
        ? azure_object_url + record.entry.invoice
        : "NA";
    },
  },

  is_invoice_uploaded: {
    header: "Invoice Uploaded",
    formatter: (_fieldValue, record) => {
      if (typeof record.entry?.is_invoice_uploaded !== "undefined") {
        return record.entry.is_invoice_uploaded ? "Yes" : "No";
      }

      return "NA";
    },
  },

  prize: {
    header: "Prize",
    formatter: (_fieldValue, record) => {
      return prizeLabels[record.prize] || record.prize || "NA";
    },
  },

  is_verified: {
    header: "Verified",
    formatter: (_fieldValue, record) => {
      if (typeof record.is_verified !== "undefined") {
        return record.is_verified ? "Yes" : "No";
      }

      return "NA";
    },
  },

  is_rejected: {
    header: "Rejected",
    formatter: (_fieldValue, record) => {
      if (typeof record.is_rejected !== "undefined") {
        return record.is_rejected ? "Yes" : "No";
      }

      return "NA";
    },
  },

  replacement_generated: {
    header: "Replacement Generated",
    formatter: (_fieldValue, record) => {
      if (typeof record.replacement_generated !== "undefined") {
        return record.replacement_generated ? "Yes" : "No";
      }

      return "NA";
    },
  },

  status: {
    header: "Status",
    formatter: (_fieldValue, record) => {
      if (record.is_verified) return "Verified";
      if (record.is_rejected) return "Rejected";

      return "Pending";
    },
  },

  winner_generated_date: {
    header: "Winner Generated Date",
    formatter: (_fieldValue, record) => {
      return record.createdAt
        ? helper.UserDate(record.createdAt, "-04:00")
        : "NA";
    },
  },

  createdAt: {
    header: "Created At",
    formatter: (_fieldValue, record) => {
      return record.entry?.createdAt
        ? helper.UserDate(record.entry.createdAt, "-04:00")
        : "NA";
    },
  },
};

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Leads</Breadcrumb.Item>
        <Breadcrumb.Item>Dream Big Win Bigger 2026</Breadcrumb.Item>
        <Breadcrumb.Item>Generate Monthly Winners</Breadcrumb.Item>
      </Breadcrumb>

      {/* ── Generate form card ─────────────────────────────────────────── */}
      <Row gutter={15}>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card title="Generate Monthly Winners" bordered={false}>
              <Form layout="inline" form={form} onFinish={handleSubmit}>
                <Form.Item
                  label="Month:"
                  name="month"
                  {...(formError.month && {
                    help: formError.month,
                    validateStatus: "error",
                  })}
                >
                  {/*
                    picker="month" renders a month-only selector.
                    format="YYYY-MM" ensures datestring matches the API requirement.
                  */}
                  <DatePicker
                    picker="month"
                    value={selectedMonth ? moment(selectedMonth, "YYYY-MM") : null}
                    format="YYYY-MM"
                    variant="borderless"
                    onChange={handleMonthChange}
                  />
                </Form.Item>

                <Form.Item>
                  <Space>
                    {/*
                      Generate button is disabled when:
                      - API is loading
                      - A batch already exists for the selected month
                    */}
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={btnloading}
                      disabled={btnloading || batchExists}
                    >
                      Generate
                    </Button>

                    {/* Informational label when batch already exists */}
                    {batchExists && (
                      <Tag color="blue">Batch already generated.</Tag>
                    )}
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </Col>
      </Row>

      {/* ── Batch summary card — only shown when batch data is available ── */}
      {batch && (
        <Row gutter={15}>
          <Col span={24}>
            <div className="site-card-border-less-wrapper">
              <Card title="Batch Summary" bordered={false}>
                <Descriptions
                  bordered
                  column={{ xxl: 4, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}
                  size="small"
                >
                  <Descriptions.Item label="Month">
                    {batch.month
                      ? moment(batch.month, "YYYY-MM").format("MMMM YYYY")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Entries">
                    {typeof batch.total_entries !== "undefined"
                      ? batch.total_entries
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Generated">
                    {typeof batch.generated_count !== "undefined"
                      ? batch.generated_count
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Verified">
                    {typeof batch.verified_count !== "undefined"
                      ? batch.verified_count
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rejected">
                    {typeof batch.rejected_count !== "undefined"
                      ? batch.rejected_count
                      : "NA"}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </div>
          </Col>
        </Row>
      )}

      {/* ── Winners table card ─────────────────────────────────────────── */}
      <Row>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <Card bordered={false}>
              <Table
                rowKey={(record) => record._id}
                columns={columns}
                dataSource={winners}
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showSizeChanger: true,
                }}
                exportableProps={{
                  fields,
                  fileName: moduleNamePural,
                }}
                searchableProps={{
                  fuzzySearch: false,
                  fuseProps: {
                    useExtendedSearch: true,
                  },
                }}
              />
            </Card>

            {/* ── Winner detail Drawer ───────────────────────────────────── */}
            <Drawer
              title="Monthly Winner Details"
              placement="right"
              onClose={handleDrawerClose}
              visible={drawerOpen}
              size="large"
            >
              {drawerWinner && (
                <Descriptions
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                >
                  <Descriptions.Item label="First Name">
                    {drawerWinner.entry?.first_name || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Name">
                    {drawerWinner.entry?.last_name || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Contact Number">
                    {drawerWinner.entry?.country_code && drawerWinner.entry?.phone
                      ? `${drawerWinner.entry.country_code} ${drawerWinner.entry.phone}`
                      : drawerWinner.entry?.phone || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email Id">
                    {drawerWinner.entry?.email || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gender">
                    {drawerWinner.entry?.gender || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">
                    {drawerWinner.entry?.date_of_birth
                      ? moment(drawerWinner.entry.date_of_birth).format("D MMMM YYYY")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address">
                    {drawerWinner.entry?.address || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country">
                    {drawerWinner.entry?.country || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="State">
                    {drawerWinner.entry?.state || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="City">
                    {drawerWinner.entry?.city
                      ? _.replace(_.deburr(drawerWinner.entry.city), /'/g, "")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Zip Code">
                    {drawerWinner.entry?.zip || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Which store did you buy this Laxmi product from?">
                    {drawerWinner.entry?.store || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="How did you get to know about this campaign?">
                    {drawerWinner.entry?.campaign_source || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="What is your favorite product from Laxmi?">
                    {drawerWinner.entry?.laxmi_fav_product || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Can you Describe Laxmi Brand in one word?">
                    {drawerWinner.entry?.laxmi_brand_one_word || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Have you seen Laxmi ads before? if yes where?">
                    {drawerWinner.entry?.laxmi_ads_desc || "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Invoice">
                    {drawerWinner.entry?.invoice ? (
                      <a
                        href={azure_object_url + drawerWinner.entry.invoice}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Invoice
                      </a>
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Invoice Uploaded">
                    {typeof drawerWinner.entry?.is_invoice_uploaded !==
                      "undefined" ? (
                      drawerWinner.entry.is_invoice_uploaded ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Prize">
                    {prizeLabels[drawerWinner.prize] ||
                      drawerWinner.prize ||
                      "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Verified">
                    {typeof drawerWinner.is_verified !== "undefined" ? (
                      drawerWinner.is_verified ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Rejected">
                    {typeof drawerWinner.is_rejected !== "undefined" ? (
                      drawerWinner.is_rejected ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Replacement Generated">
                    {typeof drawerWinner.replacement_generated !== "undefined" ? (
                      drawerWinner.replacement_generated ? (
                        <Tag color="green">Yes</Tag>
                      ) : (
                        <Tag color="red">No</Tag>
                      )
                    ) : (
                      "NA"
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Winner Generated Date">
                    {drawerWinner.createdAt
                      ? helper.UserDate(drawerWinner.createdAt, "-04:00")
                      : "NA"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {drawerWinner.entry?.createdAt
                      ? helper.UserDate(drawerWinner.entry.createdAt, "-04:00")
                      : "NA"}
                  </Descriptions.Item>
                </Descriptions>
              )}
            </Drawer>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default DiwaliBonanza2026MonthlyWinners;
